import { createSlice, createAsyncThunk, Slice } from '@reduxjs/toolkit'
import { db } from 'fb/index'
import {
  collection,
  Timestamp,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  orderBy,
} from 'firebase/firestore'
import { AppDispatch } from 'index'
import { RootState } from 'reducks/reducers'
import {
  fetchWholesalers,
  fetchWholesalerById,
  createWholesalerApi,
  updateWholesalerApi,
} from 'api/masterApi'
import { isRestMasterEnabled } from 'api/featureFlags'

interface WholesalerState {
  wholesaler: Wholesaler | null
  wholesalers: Wholesaler[]
}

interface Wholesaler {
  id: string
  name: string
  is_active: boolean
  created_at: Timestamp | null
  updated_at: Timestamp | null
}

export type WholesalerType = Wholesaler

const initialState = {
  wholesaler: null,
  wholesalers: [],
} as WholesalerState

export const getWholesalers = createAsyncThunk<
  Wholesaler[],
  { includeInactive?: boolean },
  {
    dispatch: AppDispatch
    state: RootState
  }
>('wholesaler/getWholesalers', async ({ includeInactive = false }) => {
  // Phase 3: REACT_APP_USE_REST_MASTER=true のとき daseruno-api から取得
  if (isRestMasterEnabled()) {
    const items = await fetchWholesalers(includeInactive)
    return items
      .filter((w) => w.firestore_document_id) // firestore_document_id なしは除外
      .map((w) => ({
        id: w.firestore_document_id!,
        name: w.name,
        is_active: w.is_active,
        // Timestamp は REST では取れないが、コンポーネントで参照されていないため null で代替
        created_at: null,
        updated_at: null,
      }))
  }

  // フォールバック: 従来の Firestore 読み取り
  let q
  if (includeInactive) {
    q = query(collection(db, 'wholesalers'), orderBy('name', 'asc'))
  } else {
    q = query(
      collection(db, 'wholesalers'),
      where('is_active', '==', true),
      orderBy('name', 'asc'),
    )
  }
  const querySnapshot = await getDocs(q)
  const wholesalers: Wholesaler[] = []
  querySnapshot.docs.forEach((document) => {
    const data = document.data()
    wholesalers.push({
      id: document.id,
      name: data.name,
      is_active: data.is_active,
      created_at: data.created_at,
      updated_at: data.updated_at,
    })
  })
  return wholesalers
})

export const getWholesalerById = createAsyncThunk<
  Wholesaler | null,
  { id: string },
  {
    dispatch: AppDispatch
    state: RootState
  }
>('wholesaler/getWholesalerById', async ({ id }) => {
  // Phase 3: REACT_APP_USE_REST_MASTER=true のとき daseruno-api から取得
  if (isRestMasterEnabled()) {
    const w = await fetchWholesalerById(id)
    if (!w) return null
    return {
      id: w.firestore_document_id ?? id,
      name: w.name,
      is_active: w.is_active,
      created_at: null,
      updated_at: null,
    }
  }

  // フォールバック: 従来の Firestore 読み取り
  const docRef = doc(db, 'wholesalers', id)
  const querySnapshot = await getDoc(docRef)
  const data = querySnapshot.data()
  if (!data) {
    return null
  }
  return {
    id: querySnapshot.id,
    name: data.name,
    is_active: data.is_active,
    created_at: data.created_at,
    updated_at: data.updated_at,
  }
})

export const createWholesaler = createAsyncThunk<
  string | null,
  {
    name: string
  },
  {
    dispatch: AppDispatch
    state: RootState
  }
>('wholesaler/createWholesaler', async ({ name }, thunkApi) => {
  const { staff } = thunkApi.getState().staffSlice
  if (!staff || !staff.isAdmin) {
    return null
  }

  // Phase 3: REACT_APP_USE_REST_MASTER=true のとき daseruno-api 経由
  if (isRestMasterEnabled()) {
    const w = await createWholesalerApi(name)
    return w?.firestore_document_id ?? null
  }

  // フォールバック: 従来の Firestore 書き込み
  const wholesalerRef = collection(db, 'wholesalers')
  const data = {
    name: name,
    is_active: true,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  }
  const docRef = await addDoc(wholesalerRef, data)
  return docRef.id
})

export const updateWholesaler = createAsyncThunk<
  string | null,
  {
    id: string
    name: string
    is_active: boolean
  },
  {
    dispatch: AppDispatch
    state: RootState
  }
>('wholesaler/updateWholesaler', async ({ id, name, is_active }, thunkApi) => {
  const { staff } = thunkApi.getState().staffSlice
  if (!staff || !staff.isAdmin) {
    return null
  }

  // Phase 3: REACT_APP_USE_REST_MASTER=true のとき daseruno-api 経由
  if (isRestMasterEnabled()) {
    const w = await updateWholesalerApi(id, { name, is_active })
    return w?.firestore_document_id ?? id
  }

  // フォールバック: 従来の Firestore 書き込み
  const wholesalerRef = doc(db, 'wholesalers', id)
  const data = {
    name: name,
    is_active: is_active,
    updated_at: serverTimestamp(),
  }
  await updateDoc(wholesalerRef, data)
  return id
})

export const wholesalerSlice: Slice<
  WholesalerState,
  { clearWholesaler(state: WholesalerState): void },
  'wholesaler'
> = createSlice({
  name: 'wholesaler',
  initialState,
  reducers: {
    clearWholesaler(state) {
      state.wholesaler = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWholesalers.fulfilled, (state, action) => {
        state.wholesalers = action.payload
      })
      .addCase(getWholesalerById.fulfilled, (state, action) => {
        if (action.payload !== null) {
          state.wholesaler = action.payload
        }
      })
  },
})

export const { clearWholesaler } = wholesalerSlice.actions
export default wholesalerSlice.reducer
